import type { Signal} from '@angular/core';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';

import { AppConfigService } from '../config/app-config.service';
import type { AuthClaims, AuthProfile, AuthUser, UserType } from './auth-claims.model';
import {
  IncorrectCurrentPasswordError,
  InvalidResetTokenError,
  PasswordPolicyError,
} from './password-recovery.errors';
import { ProfileValidationError } from './profile.errors';

/**
 * Clave de sessionStorage para la sesión del modo local (`auth.mode: 'local'`).
 * sessionStorage a propósito (NO localStorage): sobrevive F5 pero muere con la
 * pestaña, acotando la ventana de exposición del token.
 */
const LOCAL_SESSION_KEY = 'pvp:local-session';

/** Token pair que devuelve `POST {apiBaseUrl}/auth/login` y `/auth/refresh`. */
interface LocalTokenPair {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly tokenType?: string;
  /** Segundos de vida del access token (la API lo manda como string, ej. "900"). */
  readonly expiresIn?: string | number;
}

/** Snapshot persistido en sessionStorage para rehidratar tras F5. */
interface LocalSessionSnapshot {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Epoch ms en que expira el access token (calculado client-side). */
  readonly expiresAt: number;
}

/**
 * Respuesta de `GET {apiBaseUrl}/auth/me` (contrato: campos en español).
 *
 * `email` y `nombre` son NULLABLE: hay principals de SSO y legacy sin ellos.
 * `tipo` es el tipo de usuario, NO el rol (los roles siguen saliendo del token).
 */
interface AuthProfileDto {
  readonly id: string;
  readonly email: string | null;
  readonly nombre: string | null;
  readonly tipo: UserType;
}

/**
 * Single source of truth de la sesión, expuesta como Signals.
 *
 * Envuelve `angular-auth-oidc-client` (RxJS) y proyecta su estado a Signals en el
 * borde. El `accessToken` se mantiene sincrónico para que el authInterceptor lo
 * lea sin await. Los permisos/tenant salen de los claims firmados, no del cliente.
 * El nombre/email salen de `GET /auth/me` (el token no lleva datos personales).
 *
 * Modos (según `config.auth`): OIDC (default), local (`mode: 'local'`, login
 * email+password contra el backend) y dev-session (`devToken`, prioridad máxima).
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly oidc = inject(OidcSecurityService);
  private readonly appConfig = inject(AppConfigService);
  private readonly router = inject(Router);

  private readonly _accessToken = signal<string | null>(null);
  private readonly _claims = signal<AuthClaims | null>(null);
  private readonly _profile = signal<AuthProfile | null>(null);
  private readonly _isProfileLoading = signal(false);
  private readonly _isAuthenticated = signal(false);
  private readonly _isReady = signal(false);
  private readonly _isDevSession = signal(false);

  /** Token de acceso vigente (para el bearer). Null si no hay sesión. */
  readonly accessToken: Signal<string | null> = this._accessToken.asReadonly();
  readonly isAuthenticated: Signal<boolean> = this._isAuthenticated.asReadonly();
  /** True cuando el chequeo inicial de OIDC terminó (evita flicker en guards). */
  readonly isReady: Signal<boolean> = this._isReady.asReadonly();
  /** SOLO-DEV: true si la sesión salió de `auth.devToken` en config.json (sin IdP). */
  readonly isDevSession: Signal<boolean> = this._isDevSession.asReadonly();

  readonly claims: Signal<AuthClaims | null> = this._claims.asReadonly();
  /** Datos personales de la sesión (`GET /auth/me`). Null si aún no llegó o falló. */
  readonly profile: Signal<AuthProfile | null> = this._profile.asReadonly();
  /** True mientras `GET /auth/me` está en vuelo (la UI muestra skeleton, no un nombre falso). */
  readonly isProfileLoading: Signal<boolean> = this._isProfileLoading.asReadonly();
  readonly permissions = computed<readonly string[]>(
    () => this._claims()?.permissions ?? [],
  );
  readonly roles = computed<readonly string[]>(() => this._claims()?.roles ?? []);
  readonly tenantId = computed<string | null>(() => this._claims()?.tenantId ?? null);
  readonly isSuperAdmin = computed(() => this._claims()?.isSuperAdmin ?? false);

  /**
   * Usuario de la sesión. El `id` sale del token (siempre disponible); el nombre y
   * el email SÓLO del perfil, que es la única fuente que los tiene. Sin perfil
   * (en vuelo o fallido) van vacíos: el UUID no es un nombre y no se muestra.
   */
  readonly user = computed<AuthUser | null>(() => {
    const c = this._claims();
    if (!c) return null;
    const p = this._profile();
    return { id: c.sub, name: p?.name ?? p?.email ?? '', email: p?.email ?? '' };
  });

  /**
   * Corre el flujo de autenticación de OIDC (procesa el callback si aplica) y
   * sincroniza los Signals. Se invoca UNA vez en el APP_INITIALIZER.
   */
  async initialize(): Promise<void> {
    // Los APP_INITIALIZER async corren en paralelo: esperar la config acá
    // (load() es idempotente) en vez de confiar en el orden de providers.
    await this.appConfig.load();

    // SOLO-DEV: si config.json trae `auth.devToken`, la sesión sale de ahí y
    // NO se inicializa OIDC (el flujo normal queda intacto sin el campo).
    const devToken = this.appConfig.config().auth.devToken;
    if (devToken) {
      this.initializeDevSession(devToken);
      return;
    }

    // Modo local (`auth.mode: 'local'`): login propio contra el backend, NO se
    // inicializa OIDC. Rehidrata la sesión desde sessionStorage si sigue vigente.
    if (this.isLocalMode()) {
      await this.initializeLocalSession();
      return;
    }

    const result = await new Promise<{
      isAuthenticated: boolean;
      userData: unknown;
      accessToken: string;
    }>((resolve) => {
      this.oidc
        .checkAuth()
        .subscribe((r) => resolve(r as never));
    });

    this._isAuthenticated.set(result.isAuthenticated);
    this._accessToken.set(result.accessToken || null);
    this._claims.set(
      result.isAuthenticated ? this.mapClaims(result.userData) : null,
    );
    if (result.isAuthenticated) {
      void this.loadProfile();
    }

    // Mantener el token fresco tras silent-renew.
    this.oidc.userData$.subscribe(({ userData }) => {
      this._claims.set(userData ? this.mapClaims(userData) : null);
    });
    this.oidc.isAuthenticated$.subscribe(({ isAuthenticated }) => {
      this._isAuthenticated.set(isAuthenticated);
      if (!isAuthenticated) {
        this._accessToken.set(null);
        this._claims.set(null);
        this._profile.set(null);
      }
    });
    this.oidc.getAccessToken().subscribe((token) => {
      if (token) this._accessToken.set(token);
    });

    this._isReady.set(true);
  }

  /**
   * True si `config.auth.mode === 'local'` (login email+password propio, sin
   * OIDC). `devToken` tiene prioridad: con dev-session activa la app ya arranca
   * autenticada y este modo no llega a usarse.
   */
  isLocalMode(): boolean {
    return this.appConfig.config().auth.mode === 'local';
  }

  /** Dispara el redirect al IdP (Authorization Code + PKCE). */
  login(): void {
    if (this._isDevSession()) {
      console.warn('[auth] dev session activa');
      return;
    }
    if (this.isLocalMode()) {
      // En modo local no hay IdP: la pantalla propia maneja el ingreso.
      void this.router.navigateByUrl('/auth/login');
      return;
    }
    this.oidc.authorize();
  }

  /**
   * Login local (`mode: 'local'`): `POST {apiBaseUrl}/auth/login` con el
   * `tenantId` de config. Usa `fetch` a propósito para NO pasar por el árbol de
   * interceptores (el 401 esperado debe llegar al form como error amigable, no
   * al toast global; y no hay bearer todavía).
   *
   * Guarda el token pair en sessionStorage (rehidratación tras F5), decodifica
   * el access token con la MISMA lógica de dev-session y setea los Signals.
   *
   * @throws Error con mensaje apto para mostrar en la UI.
   */
  async loginLocal(email: string, password: string): Promise<void> {
    const cfg = this.appConfig.config();
    const tenantId = cfg.auth.tenantId;
    if (!tenantId) {
      throw new Error(
        'Falta configurar el tenant (auth.tenantId en config.json). Contactá a tu administrador.',
      );
    }

    let res: Response;
    try {
      res = await fetch(`${cfg.apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, email, password }),
      });
    } catch {
      throw new Error('No se pudo contactar al servidor. Revisá tu conexión.');
    }

    if (res.status === 401) {
      throw new Error('Credenciales inválidas');
    }
    if (!res.ok) {
      throw new Error('No se pudo iniciar sesión. Intentá de nuevo en unos minutos.');
    }

    this.applyLocalTokenPair((await res.json()) as LocalTokenPair);
  }

  /**
   * Recupero de contraseña — paso 1 (`POST {apiBaseUrl}/auth/forgot-password` con
   * `{ tenantId, email }`). Como el resto de `/auth/*`, usa `fetch` a propósito
   * (bypass del árbol de interceptores: sin bearer todavía y sin toast global).
   *
   * Anti-enumeración: el endpoint SIEMPRE responde 202 con cuerpo genérico, exista
   * o no el email. Este método NO distingue casos: resuelve en cualquier 2xx y la
   * pantalla muestra un único mensaje. Solo lanza ante errores REALES (red caída o
   * 5xx) — que no revelan si el email existe — para que la UI ofrezca reintentar.
   *
   * @throws Error con mensaje apto para la UI (red / servidor / config faltante).
   */
  async forgotPassword(email: string): Promise<void> {
    const cfg = this.appConfig.config();
    const tenantId = cfg.auth.tenantId;
    if (!tenantId) {
      throw new Error(
        'Falta configurar el tenant (auth.tenantId en config.json). Contactá a tu administrador.',
      );
    }

    let res: Response;
    try {
      res = await fetch(`${cfg.apiBaseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, email }),
      });
    } catch {
      throw new Error('No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.');
    }

    // 202 (o cualquier 2xx) = solicitud aceptada. NO se inspecciona el cuerpo:
    // es genérico por diseño (anti-enumeración). Un no-2xx es un fallo real del
    // servidor, no información sobre el email → error reintentable, sin filtrar nada.
    if (!res.ok) {
      throw new Error('No se pudo procesar la solicitud. Intentá de nuevo en unos minutos.');
    }
  }

  /**
   * Recupero de contraseña — paso 2 (`POST {apiBaseUrl}/auth/reset-password` con
   * `{ tenantId, token, newPassword }`). `fetch` a propósito: el 400 (política) y
   * el 401 (token inválido) son esperados y deben llegar al formulario, no al
   * toast global. El enlace es token-only, así que el `tenantId` sale de config
   * (el mismo que usa el login local).
   *
   * Contrato de salida:
   * - 204 / 2xx → resuelve (contraseña actualizada).
   * - 400 → {@link PasswordPolicyError} con las violaciones del servidor (autoridad).
   * - 401 → {@link InvalidResetTokenError} (token ausente/expirado/usado).
   * - red / otros 5xx → Error genérico reintentable.
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    const cfg = this.appConfig.config();
    const tenantId = cfg.auth.tenantId;
    if (!tenantId) {
      throw new Error(
        'Falta configurar el tenant (auth.tenantId en config.json). Contactá a tu administrador.',
      );
    }

    let res: Response;
    try {
      res = await fetch(`${cfg.apiBaseUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, token, newPassword }),
      });
    } catch {
      throw new Error('No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.');
    }

    if (res.ok) return;

    if (res.status === 401) {
      throw new InvalidResetTokenError();
    }
    if (res.status === 400) {
      const violations = this.extractPolicyViolations(await this.readJsonSafe(res));
      throw new PasswordPolicyError(
        violations.length > 0
          ? violations
          : ['La contraseña no cumple los requisitos de seguridad.'],
      );
    }
    throw new Error('No se pudo restablecer la contraseña. Intentá de nuevo en unos minutos.');
  }

  /**
   * Cambio de contraseña del usuario AUTENTICADO (LION-760, `POST
   * {apiBaseUrl}/auth/change-password` con `{ currentPassword, newPassword }`).
   *
   * A diferencia de forgot/reset (públicos, sin bearer), este endpoint requiere
   * el access token: se adjunta como `Authorization: Bearer` — el MISMO patrón
   * de auth-fetch que `loadProfile` (`GET /auth/me`). Usa `fetch` a propósito,
   * igual que el resto de `/auth/*` del store, para NO pasar por el árbol de
   * interceptores: acá es un REQUISITO, no una comodidad — el 401 acá significa
   * "contraseña actual incorrecta", no "access token vencido"; pasando por el
   * authRefreshInterceptor gatillaría un refresh + retry en vez de llegar al form.
   *
   * ⚠️ SWAP DE TOKENS (crítico): en 200 el backend REVOCA todas las sesiones del
   * usuario y reemite un par nuevo para el caller. Hay que REEMPLAZAR los tokens
   * guardados con el par devuelto (mismo mecanismo que `loginLocal`:
   * {@link applyLocalTokenPair}, que persiste en sessionStorage y proyecta la
   * sesión a los Signals) — si no, la próxima request viajaría con el access
   * token viejo (ya revocado) y desloguearía al usuario.
   *
   * Contrato de salida:
   * - 200 → resuelve tras swappear el par de tokens (sesión viva, ahora la única).
   * - 401 → {@link IncorrectCurrentPasswordError} (contraseña actual incorrecta o
   *   cuenta no-local; genérico, no se revela cuál).
   * - 400 → {@link PasswordPolicyError} con las violaciones del servidor
   *   (autoridad), incluido el código `SAME_AS_CURRENT` traducido al español.
   * - red / otros 5xx → Error genérico reintentable.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this._accessToken();
    if (!token) {
      throw new Error('Tu sesión expiró. Volvé a iniciar sesión.');
    }

    let res: Response;
    try {
      res = await fetch(`${this.appConfig.config().apiBaseUrl}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
    } catch {
      throw new Error('No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.');
    }

    if (res.ok) {
      // 200: el par nuevo reemplaza al viejo (revocado). Ver nota SWAP DE TOKENS.
      this.applyLocalTokenPair((await res.json()) as LocalTokenPair);
      return;
    }

    if (res.status === 401) {
      throw new IncorrectCurrentPasswordError();
    }
    if (res.status === 400) {
      const violations = this.extractPasswordViolations(await this.readJsonSafe(res));
      throw new PasswordPolicyError(
        violations.length > 0
          ? violations
          : ['La contraseña no cumple los requisitos de seguridad.'],
      );
    }
    throw new Error('No se pudo cambiar la contraseña. Intentá de nuevo en unos minutos.');
  }

  /**
   * Edición del perfil del usuario AUTENTICADO (LION-761, `PATCH
   * {apiBaseUrl}/auth/me` con `{ nombre }`). Permitido para TODOS los usuarios
   * (incluso SSO): sólo se edita el nombre para mostrar, no una credencial — por
   * eso NO se gatea por modo local.
   *
   * Mismo patrón de auth-fetch que `loadProfile`/`changePassword`: bearer con
   * `fetch` nativo, FUERA del árbol de interceptores (el 400 de validación y el
   * 401 esperados deben llegar a la pantalla, no al toast global).
   *
   * ⚠️ INTEGRACIÓN (crítico): en 200 el backend devuelve el perfil actualizado
   * `{ id, email, nombre, tipo }`. Se REEMPLAZA el signal `_profile` — la MISMA
   * fuente que puebla `loadProfile()` y que renderiza la topbar (vía el computed
   * `user`) — con el par devuelto, así el nombre del chrome se refresca al
   * instante sin recargar.
   *
   * Contrato de salida:
   * - 200 → resuelve tras actualizar `_profile` con el perfil devuelto.
   * - 401 → refresca UNA vez y reintenta (token vencido, misma política que
   *   `loadProfile`); si el token fresco también es rechazado, Error de sesión.
   * - 400 → {@link ProfileValidationError} (nombre vacío/espacios/>120) con un
   *   mensaje amigable (el del servidor si vino, o uno por defecto).
   * - red / otros 5xx → Error genérico reintentable.
   */
  async updateProfile(nombre: string): Promise<void> {
    const token = this._accessToken();
    if (!token) {
      throw new Error('Tu sesión expiró. Volvé a iniciar sesión.');
    }

    let res: Response;
    try {
      res = await this.patchProfile(token, nombre);
    } catch {
      throw new Error('No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.');
    }

    // 401: access token vencido → refrescar y reintentar UNA vez (igual que
    // loadProfile). Dev-session: refresh/logout son no-op, no tiene sentido.
    if (res.status === 401 && !this._isDevSession()) {
      let renewed: string | null;
      try {
        renewed = await this.refreshShared();
      } catch {
        // refreshShared() THROWea en un fallo TRANSITORIO del refresh (red/5xx:
        // "microcorte"/deploy-blip; ver refreshLocal). El refresh token sigue
        // vivo — no es sesión muerta. Sin este catch, el string técnico crudo de
        // refreshLocal (`refresh: HTTP 503`) llegaría al usuario; se degrada al
        // mismo error reintentable que usa el resto del store.
        throw new Error(
          'No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.',
        );
      }
      if (renewed) {
        try {
          res = await this.patchProfile(renewed, nombre);
        } catch {
          throw new Error(
            'No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.',
          );
        }
      }
    }

    if (res.ok) {
      let dto: AuthProfileDto;
      try {
        dto = (await res.json()) as AuthProfileDto;
      } catch {
        // 200 con body vacío/no-JSON: transitorio, no un perfil aplicable. Mismo
        // error reintentable que el 5xx, no un SyntaxError crudo hacia el usuario.
        throw new Error('No se pudo actualizar tu perfil. Intentá de nuevo en unos minutos.');
      }
      // La sesión pudo cambiar (logout / otro login) mientras la request volaba:
      // sólo se actualiza el perfil si sigue siendo el mismo usuario.
      if (this._claims()?.sub === dto.id) {
        this._profile.set(this.mapProfileDto(dto));
      }
      return;
    }

    if (res.status === 400) {
      const messages = this.extractPolicyViolations(await this.readJsonSafe(res));
      throw new ProfileValidationError(
        messages[0] ?? 'Ingresá un nombre válido (obligatorio, hasta 120 caracteres).',
      );
    }
    if (res.status === 401) {
      throw new Error('Tu sesión expiró. Volvé a iniciar sesión.');
    }
    throw new Error('No se pudo actualizar tu perfil. Intentá de nuevo en unos minutos.');
  }

  /** PATCH autenticado a `/auth/me` con el nombre nuevo (bearer explícito, sin interceptores). */
  private patchProfile(accessToken: string, nombre: string): Promise<Response> {
    return fetch(`${this.appConfig.config().apiBaseUrl}/auth/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ nombre }),
    });
  }

  /** Mapea el DTO de `/auth/me` (campos en español) al `AuthProfile` interno. */
  private mapProfileDto(dto: AuthProfileDto): AuthProfile {
    return {
      id: dto.id,
      email: dto.email ?? null,
      name: dto.nombre ?? null,
      tipo: dto.tipo,
    };
  }

  /** Lee el cuerpo JSON de una respuesta sin propagar un parseo fallido (body vacío/no-JSON). */
  private async readJsonSafe(res: Response): Promise<unknown> {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }

  /**
   * Extrae los mensajes de violación de política de un cuerpo de error 400. El
   * backend es la autoridad, pero el shape puede variar (envelope de Nest
   * `{ message: string | string[] }`, o `violations`/`errors` con strings u
   * objetos `{ message }`), así que se recolecta defensivamente y se deduplica.
   */
  private extractPolicyViolations(body: unknown): readonly string[] {
    if (!body || typeof body !== 'object') return [];
    const record = body as Record<string, unknown>;
    const out: string[] = [];
    const push = (value: unknown): void => {
      if (typeof value === 'string' && value.trim()) {
        out.push(value.trim());
      } else if (value && typeof value === 'object') {
        const message = (value as Record<string, unknown>)['message'];
        if (typeof message === 'string' && message.trim()) out.push(message.trim());
      }
    };
    const collect = (value: unknown): void => {
      if (Array.isArray(value)) value.forEach(push);
      else push(value);
    };
    collect(record['violations']);
    collect(record['message']);
    collect(record['messages']);
    collect(record['errors']);
    return [...new Set(out)];
  }

  /**
   * Códigos de política conocidos → mensaje en español. El único con tratamiento
   * dedicado hoy es `SAME_AS_CURRENT` (cambio de contraseña, LION-760): el backend
   * puede mandar solo el código y la UI debe mostrar un texto claro.
   */
  private static readonly POLICY_CODE_MESSAGES: Readonly<Record<string, string>> = {
    SAME_AS_CURRENT: 'La nueva contraseña debe ser distinta a la actual.',
  };

  /**
   * Violaciones de política para el cambio de contraseña (400). Procesa cada ítem
   * UNA sola vez, con prioridad al código: si el ítem trae un `code` conocido se
   * usa su traducción al español; si no, se cae a su `message` (o al string
   * pelado). NUNCA emite las dos cosas para el mismo ítem — un `WeakPasswordError`
   * del backend manda `{ code, message }` en cada violación (p.ej.
   * `{ code: 'SAME_AS_CURRENT', message: '...' }`), y mostrar ambas duplicaría el
   * bullet. También cubre `code`/`codes` pelados (solo código) y el envelope de
   * Nest `message`/`messages` (solo strings). Defensivo y deduplicado.
   */
  private extractPasswordViolations(body: unknown): readonly string[] {
    if (!body || typeof body !== 'object') return [];
    const record = body as Record<string, unknown>;
    const out: string[] = [];
    const mapCode = (value: string): string | null =>
      AuthStore.POLICY_CODE_MESSAGES[value.trim().toUpperCase()] ?? null;
    const push = (value: unknown): void => {
      if (typeof value === 'string' && value.trim()) {
        // String pelado (código o mensaje): si es un código conocido lo traduce.
        out.push(mapCode(value) ?? value.trim());
      } else if (value && typeof value === 'object') {
        const item = value as Record<string, unknown>;
        const code = typeof item['code'] === 'string' ? item['code'].trim() : '';
        const mapped = code ? mapCode(code) : null;
        if (mapped) {
          // Código conocido: gana su traducción, se ignora el `message` crudo.
          out.push(mapped);
        } else {
          const message = item['message'];
          if (typeof message === 'string' && message.trim()) out.push(message.trim());
          else if (code) out.push(code); // Código desconocido y sin mensaje: mejor el código que nada.
        }
      }
    };
    const collect = (value: unknown): void => {
      if (Array.isArray(value)) value.forEach(push);
      else push(value);
    };
    collect(record['violations']);
    collect(record['errors']);
    collect(record['code']);
    collect(record['codes']);
    collect(record['message']);
    collect(record['messages']);
    return [...new Set(out.filter((m) => m.length > 0))];
  }

  /** Cierra sesión (local + IdP en OIDC; limpia storage y vuelve al login en modo local). */
  logout(): void {
    if (this._isDevSession()) {
      console.warn('[auth] dev session activa');
      return;
    }
    if (this.isLocalMode()) {
      // Revoke the refresh family server-side BEFORE clearing local state (SA-008).
      // Best-effort: never let a failed/slow logout block the local teardown.
      const stored = this.readLocalSession();
      if (stored) {
        this.revokeLocalSession(stored.refreshToken);
      }
      sessionStorage.removeItem(LOCAL_SESSION_KEY);
      this._accessToken.set(null);
      this._claims.set(null);
      this._profile.set(null);
      this._isAuthenticated.set(false);
      void this.router.navigateByUrl('/auth/login');
      return;
    }
    this.oidc.logoff().subscribe();
    this._accessToken.set(null);
    this._claims.set(null);
    this._profile.set(null);
    this._isAuthenticated.set(false);
  }

  /**
   * Best-effort server-side revocation of the local refresh family (`POST
   * {apiBaseUrl}/auth/logout`). Uses `fetch` (bypassing the interceptor tree,
   * same reason as `loginLocal`/`refreshLocal`) with `keepalive: true` so the
   * request survives the imminent navigation. Every error is swallowed: a
   * failed or slow logout must NEVER block the local teardown. The endpoint is
   * idempotent (204 on unknown/already-revoked tokens), so a double logout — a
   * manual click plus the interceptor's `failSession()` — is safe.
   */
  private revokeLocalSession(refreshToken: string): void {
    try {
      void fetch(`${this.appConfig.config().apiBaseUrl}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // fetch threw synchronously (should not happen): teardown continues.
    }
  }

  /** Promise compartida del refresh en vuelo (single-flight, ver refreshShared). */
  private refreshInFlight: Promise<string | null> | null = null;

  /**
   * Versión single-flight de `refresh()`: si N callers piden refresh en paralelo
   * (típico: varias requests devuelven 401 a la vez), TODOS comparten la misma
   * promise y el backend recibe UN solo `/auth/refresh` (un refresh token
   * rotado dos veces quedaría revocado). Se limpia al resolver, éxito o error.
   */
  refreshShared(): Promise<string | null> {
    this.refreshInFlight ??= this.refresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  /** Refresca el access token manualmente (p.ej. tras un 401). */
  async refresh(): Promise<string | null> {
    if (this._isDevSession()) {
      console.warn('[auth] dev session activa: refresh() devuelve el mismo devToken');
      return this._accessToken();
    }
    if (this.isLocalMode()) {
      return this.refreshLocal();
    }
    // OIDC: forceRefreshSession puede ERROREAR (IdP caído, refresh vencido).
    // La promise debe settlearse SIEMPRE — sin error handler quedaría colgada
    // y el single-flight de refreshShared() nunca se liberaría (deadlock de
    // todos los 401 posteriores). Error → null: sesión irrecuperable, el
    // caller decide (logout + login).
    try {
      const result = await new Promise<{ accessToken: string }>((resolve, reject) => {
        this.oidc.forceRefreshSession().subscribe({
          next: (r) => resolve(r as never),
          error: reject,
        });
      });
      const token = result.accessToken || null;
      this._accessToken.set(token);
      return token;
    } catch {
      console.warn('[auth] refresh OIDC falló: sesión irrecuperable');
      return null;
    }
  }

  /**
   * Refresh del modo local: canjea el refresh token por un token pair nuevo en
   * `POST {apiBaseUrl}/auth/refresh`.
   *
   * Contrato: token en éxito; `null` cuando la sesión está MUERTA (401/403 del
   * endpoint → refresh vencido/revocado; cierra la sesión); THROW en fallos
   * transitorios (red caída, 5xx de un deploy) — el refresh token de 30 días
   * sigue siendo canjeable y NO hay que destruir la sesión por un microcorte.
   */
  private async refreshLocal(): Promise<string | null> {
    const stored = this.readLocalSession();
    if (!stored) return null;
    let res: Response;
    try {
      res = await fetch(`${this.appConfig.config().apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: stored.refreshToken }),
      });
    } catch (error) {
      console.warn('[auth] refresh local: error de red; la sesión se conserva');
      throw error instanceof Error ? error : new Error('refresh: network error');
    }
    if (res.status === 401 || res.status === 403) {
      console.warn('[auth] refresh local rechazado: cerrando sesión');
      this.logout();
      return null;
    }
    if (!res.ok) {
      console.warn(`[auth] refresh local HTTP ${res.status}; la sesión se conserva`);
      throw new Error(`refresh: HTTP ${res.status}`);
    }
    this.applyLocalTokenPair((await res.json()) as LocalTokenPair);
    return this._accessToken();
  }

  /**
   * Arranque del modo local: si hay una sesión vigente en sessionStorage la
   * rehidrata (F5 no desloguea). Con el access token vencido pero refresh token
   * presente, intenta renovar ANTES de descartar — el refresh token vive 30
   * días y un F5 tras 16 minutos idle no debe obligar a re-tipear credenciales.
   * 401/403 del refresh → refreshLocal ya limpió; fallo transitorio → conserva
   * el snapshot (reintentable en el próximo arranque) y arranca desautenticado.
   */
  private async initializeLocalSession(): Promise<void> {
    const stored = this.readLocalSession();
    if (stored) {
      if (stored.expiresAt > Date.now()) {
        this.setLocalSession(stored.accessToken);
      } else {
        try {
          await this.refreshLocal();
        } catch {
          // Transitorio (red/5xx): snapshot intacto; el guard manda al login.
        }
      }
    }
    this._isReady.set(true);
  }

  /** Persiste el token pair y proyecta la sesión a los Signals. */
  private applyLocalTokenPair(pair: LocalTokenPair): void {
    const snapshot: LocalSessionSnapshot = {
      accessToken: pair.accessToken,
      refreshToken: pair.refreshToken,
      expiresAt: this.resolveLocalExpiry(pair),
    };
    sessionStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify(snapshot));
    this.setLocalSession(pair.accessToken);
  }

  /**
   * Setea los Signals de sesión desde un access token local (login, rehidratación
   * o rotación por refresh) y dispara la carga del perfil — `loadProfile()` es
   * idempotente por `sub`, así que el refresh periódico no re-pega a `/auth/me`.
   */
  private setLocalSession(accessToken: string): void {
    this._accessToken.set(accessToken);
    this._claims.set(this.mapClaims(this.decodeJwtPayload(accessToken)));
    this._isAuthenticated.set(true);
    this._isReady.set(true);
    void this.loadProfile();
  }

  /**
   * Calcula el epoch ms de expiración del access token local.
   *
   * Preferiría el claim `exp`, pero la API local emite `exp === iat` (token
   * "nace vencido" según su propio claim) aunque `expiresIn` diga 900s. Por eso
   * `exp` solo se usa si es futuro; si no, se cae a `expiresIn` (default 900s).
   */
  private resolveLocalExpiry(pair: LocalTokenPair): number {
    const payload = this.decodeJwtPayload(pair.accessToken);
    const exp = typeof payload?.['exp'] === 'number' ? payload['exp'] * 1000 : 0;
    if (exp > Date.now()) return exp;
    const seconds = Number(pair.expiresIn);
    return Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds : 900) * 1000;
  }

  /** Lee y valida el snapshot de sessionStorage. Corrupto → null (y se limpia). */
  private readLocalSession(): LocalSessionSnapshot | null {
    const raw = sessionStorage.getItem(LOCAL_SESSION_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<LocalSessionSnapshot>;
      if (
        typeof parsed.accessToken === 'string' &&
        typeof parsed.refreshToken === 'string' &&
        typeof parsed.expiresAt === 'number'
      ) {
        return parsed as LocalSessionSnapshot;
      }
    } catch {
      // JSON corrupto: cae al removeItem de abajo.
    }
    sessionStorage.removeItem(LOCAL_SESSION_KEY);
    return null;
  }

  /**
   * SOLO-DEV: arranca autenticado con el JWT de `config.auth.devToken`.
   *
   * Decodifica el payload client-side SIN validar firma (la valida el backend)
   * y mapea claims con el MISMO mapper del flujo OIDC. No toca OIDC: sin
   * `devToken` en config, el flujo productivo queda exactamente igual.
   */
  private initializeDevSession(devToken: string): void {
    console.warn(
      '[auth] DEV SESSION activa: token tomado de config.json (auth.devToken), ' +
        'sin IdP. NUNCA usar en despliegues.',
    );
    this._isDevSession.set(true);
    this._accessToken.set(devToken);
    this._claims.set(this.mapClaims(this.decodeJwtPayload(devToken)));
    this._isAuthenticated.set(true);
    this._isReady.set(true);
    void this.loadProfile();
  }

  /** `sub` cuyo perfil está en vuelo (single-flight, ver loadProfile). */
  private profileInFlight: string | null = null;

  /**
   * Carga el perfil del usuario autenticado desde `GET {apiBaseUrl}/auth/me`
   * (`apiBaseUrl` ya viene versionada: el path efectivo es `/api/v1/auth/me`).
   *
   * El token sólo lleva ids opacos, roles y permisos — CERO datos personales —,
   * así que el nombre y el email sólo pueden venir del backend. Se llama al
   * establecerse la sesión: login local, rehidratación desde sessionStorage,
   * dev-session y checkAuth de OIDC.
   *
   * Usa `fetch` por el MISMO motivo que loginLocal/refreshLocal/logout: los
   * llamados a `/auth/*` de este store quedan FUERA del árbol de interceptores.
   * Acá es un requisito, no una comodidad: pasando por HttpClient, un 5xx o un
   * body inesperado levantarían un toast global por una request de fondo, y el
   * 401 lo gobernaría el authRefreshInterceptor en vez de este store.
   *
   * Fallos, con semántica DISTINTA (ver también refreshLocal):
   * - Red caída, 5xx, body inesperado → TRANSITORIO: perfil en null, sesión
   *   INTACTA (la UI cae a su placeholder). Se reintenta en la próxima sesión.
   * - 401 → credencial MUERTA (token inválido, o principal borrado/suspendido/de
   *   otro tenant). No es "perfil no disponible": refresca UNA vez y reintenta —
   *   misma política que authRefreshInterceptor, un access token vencido no debe
   *   desloguear — y si el token fresco también es rechazado, cierra la sesión.
   *
   * Idempotente por `sub`: una rotación de token no vuelve a pegarle (el refresh
   * de acá reentra y sale por la guarda), pero un intento fallido se reintenta.
   */
  private async loadProfile(): Promise<void> {
    const token = this._accessToken();
    const sub = this._claims()?.sub;
    if (!token || !sub) return;
    if (this._profile()?.id === sub || this.profileInFlight === sub) return;

    this.profileInFlight = sub;
    this._isProfileLoading.set(true);
    try {
      let res = await this.fetchProfile(token);

      // Dev-session: refresh() devuelve el mismo devToken y logout() es no-op —
      // reintentar/desloguear no haría nada. El 401 queda como aviso.
      if (res.status === 401 && !this._isDevSession()) {
        // refreshShared(): token → reintento; null → refreshLocal YA cerró la
        // sesión; throw → fallo transitorio del refresh (lo toma el catch).
        const renewed = await this.refreshShared();
        if (!renewed) return;
        res = await this.fetchProfile(renewed);
        if (res.status === 401) {
          console.warn('[auth] /auth/me rechazó el token recién refrescado: sesión muerta');
          this.logout();
          return;
        }
      }

      if (!res.ok) {
        console.warn(`[auth] /auth/me HTTP ${res.status}: la sesión sigue viva, sin perfil`);
        return;
      }

      const dto = (await res.json()) as AuthProfileDto;
      // La sesión pudo cambiar (logout / otro login) mientras la request volaba.
      if (this._claims()?.sub !== sub) return;
      this._profile.set(this.mapProfileDto(dto));
    } catch {
      console.warn('[auth] /auth/me falló: la sesión sigue viva, sin perfil');
    } finally {
      this.profileInFlight = null;
      this._isProfileLoading.set(false);
    }
  }

  private fetchProfile(accessToken: string): Promise<Response> {
    return fetch(`${this.appConfig.config().apiBaseUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  /**
   * Decodifica el payload (segmento del medio) de un JWT, base64url → JSON.
   * Sin validación de firma a propósito (solo-dev; el backend autoriza).
   */
  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    const segment = token.split('.')[1];
    if (!segment) return null;
    try {
      const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const parsed: unknown = JSON.parse(atob(padded));
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      console.warn('[auth] devToken inválido: no se pudo decodificar el payload JWT');
      return null;
    }
  }

  private mapClaims(userData: unknown): AuthClaims {
    const cfg = this.appConfig.config().auth;
    const raw = (userData ?? {}) as Record<string, unknown>;

    const tenantClaim = cfg.tenantClaim ?? 'tenant_id';
    const permsClaim = cfg.permissionsClaim ?? 'permissions';
    const rolesClaim = cfg.rolesClaim ?? 'roles';

    const roles = this.toStringArray(raw[rolesClaim]);

    // Sin name/email: el token NO transporta datos personales. El perfil sale de
    // `/auth/me` (ver loadProfile) — el `sub` es un UUID opaco, no un nombre.
    return {
      sub: String(raw['sub'] ?? ''),
      tenantId: String(raw[tenantClaim] ?? ''),
      roles,
      permissions: this.toStringArray(raw[permsClaim]),
      // El backend firma el claim `roles` con el NOMBRE crudo del rol RBAC
      // (issue-tokens: `roles.map(r => r.name)`); el rol de plataforma se llama
      // `super_admin` (guion bajo, ver tenant-provisioning). `is_super_admin`
      // queda como escape-hatch por si el IdP lo mapea como claim propio.
      isSuperAdmin: raw['is_super_admin'] === true || roles.includes('super_admin'),
    };
  }

  private toStringArray(value: unknown): readonly string[] {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string' && value.length > 0) return value.split(/[\s,]+/);
    return [];
  }
}
